// src/utils/axiosInstance.js
import axios from 'axios'

// Helper functions to get/store token & user from localStorage
function getToken() {
  return localStorage.getItem('token')
}
function getUser() {
  const rawUser = localStorage.getItem('user') || '{}'
  return JSON.parse(rawUser)
}
function storeToken(newToken) {
  localStorage.setItem('token', newToken)
}
function storeUser(newUser) {
  localStorage.setItem('user', JSON.stringify(newUser))
}

// A helper to forcibly log the user out if refresh fails
function forceLogout() {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  // Broadcast so other tabs know
  window.postMessage('logout_due_to_expiry', '*')            // ← NEW
  // Redirect them to your specific domain
  window.location.href = 'https://you.payme.co.jp'
}

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: 'https://payme-backend-713827320947.asia-northeast1.run.app',
})

// Track whether we're currently refreshing & store a queue of requests
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// 1) REQUEST INTERCEPTOR: Attach the token to every request if available
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 2) RESPONSE INTERCEPTOR: If we get 401, attempt token refresh
axiosInstance.interceptors.response.use(
  (response) => response, // Pass through all successful responses
  async (error) => {
    const originalRequest = error.config

    /* Only try refresh when:
       • status = 401
       • server explicitly said token expired                 */
    if (
      !error.response ||
      error.response.status !== 401 ||
      error.response.data?.error !== 'token_expired'          // ← CHANGED
    ) {
      return Promise.reject(error)
    }

    // Prevent infinite loop
    if (originalRequest._retry) {
      return Promise.reject(error)
    }
    originalRequest._retry = true

    // If we’re already refreshing, queue the request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return axiosInstance(originalRequest)
        })
        .catch((err) => Promise.reject(err))
    }

    isRefreshing = true

    try {
      const oldToken = getToken()
      const user = getUser()
      if (!user.id) throw new Error('No user ID stored for refresh.')

      // Attempt token refresh
      const refreshRes = await axios.post(
        // backend route is /refresh-token, not /refresh
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/refresh-token', // ← CHANGED
        { userId: user.id },
        { headers: { Authorization: `Bearer ${oldToken}` } }
      )

      const { token: newToken, user: updatedUser } = refreshRes.data || {}
      if (!newToken) throw new Error('No new token returned from refresh.')

      // Store new token & user
      storeToken(newToken)
      storeUser(updatedUser)

      // Let all queued requests continue with the new token
      processQueue(null, newToken)

      // Retry the original request with fresh token
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return axiosInstance(originalRequest)
    } catch (refreshErr) {
      console.error('Token refresh failed:', refreshErr)
      processQueue(refreshErr, null)
      forceLogout()
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  }
)

export default axiosInstance
